import { isArray, get, isEqual } from 'lodash';
import { makeAutoObservable } from 'mobx';
import { composeLocationText } from '../utils/formatters';
import { isBrowser } from '../utils/detectEnv';

const LOCATION_KEY = 'CUSTOMER_GEOLOCATION_INFO';

const defaultFilterSettings = {
  availability: [],
  employmentTypes: [],
  tradeTypes: [],
  yearsOfExperience: [],
  militaryStatuses: [],
  languages: [],
  query: '',
  zipCode: '',
  distance: '',
  coordinates: ''

};

const defaultPills = [];

class FilterStore {

  // pill: {field: string, value: string}
  pills = [...defaultPills];

  sharedLocationObject = {};

  // text used in location filter
  sharedLocationText = '';

  filterSettings = { ...defaultFilterSettings };

  filterMetadata = {};

  mobileFilterDrawerOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  toggleMobileFilterDrawerOpen = () => {
    if (window.screen.width <= 640) {
      this.mobileFilterDrawerOpen = !this.mobileFilterDrawerOpen;
    }
  };

  __updateSharedLocationText = (text) => {
    this.sharedLocationText = text;
  };

  __updateSharedLocationObject = (value) => {
    this.sharedLocationObject = value;
  };

  setProximityFilter = (locationObject, distance = '50mi') => {

    sessionStorage.setItem(LOCATION_KEY, JSON.stringify({ ...locationObject, status: 'ACCEPTED' }));

    this.__updateSharedLocationObject(locationObject);

    this.__updateSharedLocationText(composeLocationText(locationObject.zipCode, locationObject.city, locationObject.state));
    this.addLocationFilter(locationObject);
    distance && this.addFilterParam('distance', distance);
  };

  addLocationFilter(locationObject) {
    this._removeLocationPills();

    if (get(locationObject, 'geolocation.latitude') || get(locationObject, 'geolocation.longitude')) {
      this._updateFilterSettingsField('coordinates', `${locationObject?.geolocation?.latitude},${locationObject?.geolocation.longitude}`);

      this._addPill({ field: 'location', value: composeLocationText(locationObject.zipCode, locationObject.city, locationObject.state) });
      this._storeFilterSettingStorage('coordinates', `${locationObject?.geolocation?.latitude},${locationObject?.geolocation.longitude}`);
    }
  }

  _removeLocationPills() {
    this.pills = this.pills.filter((it) => {
      return get(it, 'field') !== 'location' && get(it, 'field') !== 'distance';
    });

    this._updateFilterSettingsField('distance', '');

  }

  resetProximityFilter = (declinedModal) => {
    if (declinedModal) {
      sessionStorage.setItem(LOCATION_KEY, JSON.stringify({ status: 'DECLINED' }));
    } else {
      sessionStorage.removeItem(LOCATION_KEY);
    }

    this.__updateSharedLocationText('');
    this.__updateSharedLocationObject({});
    this.removeFilterParam('coordinates');
    this._removeLocationPills();
  };

  setFilterMetadata = (metadata) => {
    this.filterMetadata = metadata;
  };

  /**
   * if the field is an array of values, add value to array and add the pill
   * if the field is not an array, update the field with new value and update or add pill
   * @param field
   * @param value
   */
  addFilterParam = (field, value) => {
    if (isArray(this.filterSettings[field])) {
      const newValue = [...this.filterSettings[field], value];
      this._updateFilterSettingsField(field, newValue);
      this._addPill({ field, value });
    } else {
      this._updateFilterSettingsField(field, value);

      this._updatePill(field, value);
    }
    this._storeFilterSettingStorage(field, value);
  };

  _updatePill = (field, value) => {
    const pill = this.pills.find((it) => it.field === field);

    if (pill) {
      pill.value = value;
    } else {
      this._addPill({ field, value });
    }
  };

  removeFilterParam = (field, value) => {
    if (field === 'location' || field === 'distance') {
      this.resetProximityFilter();
    }
    if (isArray(this.filterSettings[field])) {
      const newValue = this.filterSettings[field].filter((it) => it !== value);
      this._updateFilterSettingsField(field, newValue);
      this._removePill({ field, value });
    } else {
      // fields with primitive value (e.g. zipCode, query, distance)
      const oldValue = this.filterSettings[field];
      this._updateFilterSettingsField(field, '');
      this._removePill({ field, value: oldValue });
    }
    this.saveFilterSettings();
  };

  clearAll = () => {
    this.filterSettings = {
      ...defaultFilterSettings
    };
    this.pills = [...defaultPills];
    this.resetProximityFilter();
    this.saveFilterSettings();
  };

  _updateFilterSettingsField = (field, value) => {
    this.filterSettings = {
      ...this.filterSettings,
      [field]: value
    };
  };

  _addPill = (pill) => {
    this.pills.push(pill);
  };

  _removePill = (pill) => {
    this.pills = this.pills.filter((it) => !isEqual(it, pill));
  };

  saveFilterSettings = () => {
    if (!isBrowser()) {
      return;
    }

    sessionStorage.setItem('searchSession', JSON.stringify(this.filterSettings));
  };

  _storeFilterSettingStorage = (field, value) => {
    console.log('storing field');
    if (!isBrowser()) {
      return;
    }

    try {
      const storedSession = JSON.parse(sessionStorage.getItem('searchSession')) || {};

      if (isArray(this.filterSettings[field])) {
        // Field is an array — only add value if it doesn't already exist
        const currentArray = storedSession[field] || [];
        if (!currentArray.includes(value)) {
          currentArray.push(value);
          storedSession[field] = currentArray;
          sessionStorage.setItem('searchSession', JSON.stringify(storedSession));
        }
      } else {
        // Field is a primitive (string, int, etc.) — replace the value
        storedSession[field] = value;
        sessionStorage.setItem('searchSession', JSON.stringify(storedSession));
      }
    } catch (error) {
      console.error('Error storing filter setting:', error);
    }
  };

  restoreFilterSettings = () => {
    if (!isBrowser()) {
      return;
    }

    const saved = sessionStorage.getItem('searchSession');
    if (!saved) {
      return;
    }

    try {
      const restoredSettings = JSON.parse(saved);

      if (!restoredSettings || typeof restoredSettings !== 'object') {
        return;
      }

      this.filterSettings = {
        ...defaultFilterSettings
      };
      this.pills = [...defaultPills];

      Object.entries(restoredSettings).forEach(([field, value]) => {
        this._updateFilterSettingsField(field, value);

        if (isArray(value)) {
          value.forEach((itemValue) => {
            if (itemValue) {
              this._addPill({ field, value: itemValue });
            }
          });
          return;
        }

        // Location is rendered via a dedicated location pill, not zip/coords pills.
        if (field === 'zipCode' || field === 'coordinates') {
          return;
        }

        if (value) {
          this._updatePill(field, value);
        }
      });

      const savedLocation = sessionStorage.getItem(LOCATION_KEY);
      if (!savedLocation) {
        return;
      }

      const parsedLocation = JSON.parse(savedLocation);
      if (parsedLocation?.status !== 'ACCEPTED') {
        return;
      }

      this.__updateSharedLocationObject(parsedLocation);

      const locationText = composeLocationText(parsedLocation.zipCode, parsedLocation.city, parsedLocation.state);
      this.__updateSharedLocationText(locationText);

      if (locationText) {
        this._updatePill('location', locationText);
      }
      console.log('restored with object: ', restoredSettings);
    } catch (error) {
      console.error('Error restoring filter settings:', error);
    }
  };
}

export default new FilterStore();
